import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/shared";
import { toast } from "sonner";
import { Save, ShieldCheck, Send, UserPlus, Trash2, Users } from "lucide-react";

export default function Settings() {
  const { isAdmin, user } = useAuth();
  const [form, setForm] = useState(null);
  const [savedSalesmsg, setSavedSalesmsg] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [users, setUsers] = useState([]);
  const [userOpen, setUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "staff" });

  const loadUsers = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/auth/users");
      setUsers(r.data);
    } catch {
      /* not admin */
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data;
      setSavedSalesmsg(s.salesmsg_pat_masked || "");
      setForm({ ...s, salesmsg_pat: "" });
    });
    loadUsers();
    // eslint-disable-next-line
  }, [isAdmin]);

  const save = async () => {
    try {
      const payload = { ...form };
      delete payload.salesmsg_pat_masked;
      delete payload.id;
      payload.default_daily_rate = Number(payload.default_daily_rate || 0);
      payload.default_weekly_rate = Number(payload.default_weekly_rate || 0);
      payload.default_deposit = Number(payload.default_deposit || 0);
      // Empty PAT: backend will preserve existing value
      const res = await api.put("/settings", payload);
      toast.success("Settings saved");
      setSavedSalesmsg(res.data.salesmsg_pat ? ("•".repeat(8) + res.data.salesmsg_pat.slice(-6)) : "");
      setForm({ ...res.data, salesmsg_pat: "" });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sendTest = async () => {
    try {
      if (!testTo) return toast.error("Phone number required");
      const r = await api.post("/sms/test", { to: testTo });
      if (r.data?.status === "sent") toast.success("Test SMS sent! Check your phone.");
      else if (r.data?.status === "skipped") toast.warning("Salesmsg not configured · save creds first");
      else toast.error(r.data?.error || "Send failed");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const createUser = async () => {
    try {
      if (!newUser.email || !newUser.password || !newUser.name) {
        return toast.error("All fields required");
      }
      await api.post("/auth/register", newUser);
      toast.success("User created");
      setNewUser({ email: "", password: "", name: "", role: "staff" });
      setUserOpen(false);
      loadUsers();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/auth/users/${uid}`);
      toast.success("User deleted");
      loadUsers();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!form) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader title="Settings" subtitle="Business config" testId="settings-page-header" />

      {!isAdmin && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 text-sm">
          You&apos;re signed in as Staff. Only Admins can change settings.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Business</h3>
          <div className="space-y-3">
            <div>
              <Label>Business name</Label>
              <Input data-testid="set-biz-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} disabled={!isAdmin} />
            </div>
            <div>
              <Label>Business address</Label>
              <Input data-testid="set-biz-address" value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} disabled={!isAdmin} />
            </div>
            <div>
              <Label>Business phone</Label>
              <Input data-testid="set-biz-phone" value={form.business_phone} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} disabled={!isAdmin} />
            </div>
            <div>
              <Label>Google review link</Label>
              <Input data-testid="set-google-review" value={form.google_review_link} onChange={(e) => setForm({ ...form, google_review_link: e.target.value })} disabled={!isAdmin} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="payment-methods-card">
          <h3 className="font-heading text-lg font-semibold mb-1">Payment collection</h3>
          <p className="text-xs text-gray-500 mb-4">
            These handles are shown on the public <span className="font-mono">/renew/</span> page when customers tap the renewal link.
            Blank fields are simply hidden.
          </p>
          <div className="space-y-3">
            <div>
              <Label>Zelle handle</Label>
              <Input
                data-testid="set-zelle-handle"
                value={form.zelle_handle || ""}
                onChange={(e) => setForm({ ...form, zelle_handle: e.target.value })}
                placeholder="you@example.com or +1 267 555 0100"
                disabled={!isAdmin}
              />
            </div>
            <div>
              <Label>Cash App handle</Label>
              <Input
                data-testid="set-cashapp-handle"
                value={form.cashapp_handle || ""}
                onChange={(e) => setForm({ ...form, cashapp_handle: e.target.value })}
                placeholder="$YourCashtag"
                disabled={!isAdmin}
              />
            </div>
            <div>
              <Label>Extra payment instructions (optional)</Label>
              <Input
                data-testid="set-payment-instructions"
                value={form.payment_instructions || ""}
                onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })}
                placeholder="e.g. Include your name in the memo"
                disabled={!isAdmin}
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2">Salesmsg SMS</h3>
          <p className="text-xs text-gray-500 mb-4">Enter your Salesmsg PAT + Team ID to enable automated SMS. Generate a PAT at app.salesmessage.com → Settings → Personal Access Tokens.</p>
          <div className="space-y-3">
            <div>
              <Label>From number (Salesmsg)</Label>
              <Input data-testid="set-salesmsg-from" value={form.salesmsg_from_number || ""} onChange={(e) => setForm({ ...form, salesmsg_from_number: e.target.value })} placeholder="+12157700679" disabled={!isAdmin} />
            </div>
            <div>
              <Label>Team / Inbox ID</Label>
              <Input data-testid="set-salesmsg-team" value={form.salesmsg_team_id || ""} onChange={(e) => setForm({ ...form, salesmsg_team_id: e.target.value })} placeholder="259597" disabled={!isAdmin} />
            </div>
            <div>
              <Label>Personal Access Token</Label>
              <Input
                data-testid="set-salesmsg-pat"
                type="password"
                value={form.salesmsg_pat || ""}
                onChange={(e) => setForm({ ...form, salesmsg_pat: e.target.value })}
                placeholder={savedSalesmsg || "Paste your Salesmsg PAT"}
                disabled={!isAdmin}
              />
              {savedSalesmsg && !form.salesmsg_pat && (
                <p className="text-xs text-gray-500 mt-1">Current: {savedSalesmsg} — leave blank to keep, or retype to update.</p>
              )}
            </div>
            <div className="pt-2 border-t border-gray-100">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={!isAdmin || !((savedSalesmsg || form.salesmsg_pat) && form.salesmsg_team_id)}
                onClick={() => setTestOpen(true)}
                data-testid="open-test-sms"
              >
                <Send size={12} className="mr-1.5" /> Send test SMS
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Defaults</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Daily rate</Label>
              <Input type="number" step="0.01" data-testid="set-daily" value={form.default_daily_rate} onChange={(e) => setForm({ ...form, default_daily_rate: e.target.value })} disabled={!isAdmin} />
            </div>
            <div>
              <Label>Weekly rate</Label>
              <Input type="number" step="0.01" data-testid="set-weekly" value={form.default_weekly_rate} onChange={(e) => setForm({ ...form, default_weekly_rate: e.target.value })} disabled={!isAdmin} />
            </div>
            <div>
              <Label>Deposit</Label>
              <Input type="number" step="0.01" data-testid="set-deposit" value={form.default_deposit} onChange={(e) => setForm({ ...form, default_deposit: e.target.value })} disabled={!isAdmin} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col">
          <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><ShieldCheck size={18} /> Status</h3>
          <ul className="text-sm space-y-2">
            <li className="flex items-center justify-between"><span>Salesmsg configured</span><span className={(savedSalesmsg || form.salesmsg_pat) && form.salesmsg_team_id ? "text-green-700 font-medium" : "text-amber-700 font-medium"}>{(savedSalesmsg || form.salesmsg_pat) && form.salesmsg_team_id ? "Yes" : "No"}</span></li>
            <li className="flex items-center justify-between"><span>Google review link</span><span className={form.google_review_link ? "text-green-700 font-medium" : "text-amber-700 font-medium"}>{form.google_review_link ? "Set" : "Missing"}</span></li>
            <li className="flex items-center justify-between"><span>Business address</span><span className={form.business_address ? "text-green-700 font-medium" : "text-amber-700 font-medium"}>{form.business_address ? "Set" : "Missing"}</span></li>
          </ul>
          <div className="mt-auto pt-4">
            <Button onClick={save} disabled={!isAdmin} className="bg-black hover:bg-gray-800 text-white w-full" data-testid="settings-save-button">
              <Save size={14} className="mr-2" /> Save Settings
            </Button>
          </div>
        </div>
      </div>

      {/* User Management (admin only) */}
      {isAdmin && (
        <div className="mt-4 bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <Users size={18} /> Team Members
              </h3>
              <p className="text-xs text-gray-500 mt-1">Create admin and staff accounts.</p>
            </div>
            <Button onClick={() => setUserOpen(true)} className="bg-black hover:bg-gray-800 text-white" data-testid="add-user-button">
              <UserPlus size={14} className="mr-2" /> Add user
            </Button>
          </div>
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-testid="users-list">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{u.name}</td>
                    <td className="px-4 py-2">{u.email}</td>
                    <td className="px-4 py-2">
                      <span className={`pill ${u.role === "admin" ? "pill-blue" : "pill-gray"}`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {u.id !== user?.id && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Test SMS dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="test-sms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Test SMS</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Recipient phone (E.164)</Label>
            <Input data-testid="test-sms-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+15551234567" />
            <p className="text-xs text-gray-500 mt-2">A short test message will be sent to verify your Salesmsg setup.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={() => { sendTest(); setTestOpen(false); }} className="bg-black hover:bg-gray-800 text-white" data-testid="test-sms-send">
              <Send size={14} className="mr-2" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New user dialog */}
      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" data-testid="new-user-email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" data-testid="new-user-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (cannot delete records)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserOpen(false)}>Cancel</Button>
            <Button onClick={createUser} className="bg-black hover:bg-gray-800 text-white" data-testid="new-user-save">
              <UserPlus size={14} className="mr-2" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
